// Otto's half of the app's 401 handling.
//
// Every other feature talks to the three gateways in `services/authorizationService.js`, which have
// carried a refresh-and-replay interceptor for years: a 401 refreshes the session once and re-issues the
// request, and only a failed refresh logs the user out. Otto talks to a FOURTH backend (filter-ai) through
// its own axios clients, which had no such interceptor — harmless while that backend accepted every
// request, and load-bearing the moment its auth gate was turned on after the 2026-08-31 pen-test report.
// Without this, an expired access token makes Otto — and only Otto — fail: the chat answers "something
// went wrong", the AI Settings pages show empty tables, and a reload appears to fix it. That looks like an
// Otto bug and is not one.
//
// The refresh itself is NOT reimplemented here. `refreshAuthSession` is the app's single flight, shared
// with the three gateways: a private copy would let two refreshes race, and the loser revokes the token
// the winner just minted.
//
// Attach AFTER any transport-retry interceptor on the same instance (they run in registration order), so a
// dropped connection is still retried as a transport glitch rather than mistaken for an auth failure.
export const attachOttoAuthRetry = instance => {
    instance.interceptors.response.use(null, async error => {
        const originalRequest = error.config;

        // Once per request. A 401 that survives a successful refresh is a real denial — most likely a 403
        // in disguise (no Otto grant for this page), and retrying it forever would spin.
        if (error.response?.status !== 401 || !originalRequest || originalRequest._ottoAuthRetried) {
            return Promise.reject(error);
        }

        originalRequest._ottoAuthRetried = true;
        // Required HERE, not imported at the top, and the reason is not style. `authorizationService`
        // pulls in `config/firebase`, which initializes Firebase messaging at module load — importing it
        // from a core Otto module drags that whole stack into every consumer (three Otto test suites
        // stopped even loading: "Cannot read properties of undefined (reading 'addEventListener')"). A
        // 401 is rare and already a network round-trip, so paying the load then costs nothing and keeps
        // Otto's core independently loadable. Same lazy-require reason as core/telemetry.js.
        // eslint-disable-next-line global-require
        const { refreshAuthSession } = require("../../../services/authorizationService");
        await refreshAuthSession(); // rejects (and logs out) when the session is genuinely gone
        return instance(originalRequest);
    });
};
